import Cookies from 'js-cookie'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Card } from 'react-bootstrap'
import config from '../../config'
import useDebounce from '../../hooks/useDebounce'
import { Pagination } from './components/Pagination'
import PaymentDetailModal from './components/PaymentDetailModal'
import PaymentFilter from './components/PaymentFilter'
import {
  INITIAL_SEARCH_PARAMS,
  ORDER_TYPE,
  PAYMENT_ACTION_STATUS,
  PAYMENT_STATUS,
  SLIP_IN_PROGRESS_STATUS,
  SORT_ORDER,
} from './constants'
import { ButtonCancelOrder, PageContainer, StatusText, StyledTable } from './styles'
import {
  getBackgroundColorByActionStatus,
  getBackgroundColorByStatus,
  getTextColorByStatus,
  isJsonString,
} from './utils'
import { connectWs, sendMessageWs } from './utils/websocket'
import QRPaymentDetailModal from './components/QRPaymentDetailModal'
import { useSelector } from 'react-redux'
import Swal from 'sweetalert2'
import Colors from '../../constants/Colors'

const Payment = () => {
  const [payments, setPayments] = useState([])
  const [hasNextPage, setHasNextPage] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [searchParams, setSearchParams] = useState(INITIAL_SEARCH_PARAMS)
  const [qrTransactionNo, setQrTransactionNo] = useState('')
  const [isShowQrModal, setIsShowQrModal] = useState(false)
  const { isRoundClosed } = useSelector((state) => state.webConfig)
  const wsRef = useRef(null)

  const fetchPayments = useCallback(async (searchParams) => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams(searchParams).toString()
      const response = await fetch(`${config.apiAdminGateway}/api/v2/admin/payments?${params}`, {
        headers: {
          Authorization: `Bearer ${Cookies.get('accessToken')}`,
        },
      }).then((res) => res.json())

      if (response.status.code !== 0) throw new Error(response.status.description)
      setPayments(response.data)
      setHasNextPage(response.pagination.hasNext)
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const debouncedFetchPayments = useDebounce(fetchPayments, 500)

  const handleUpdateSearchParams = useCallback(
    (key, value) => {
      switch (key) {
        case 'status':
          setSearchParams((params) => ({
            ...params,
            page: 1,
            inProgress: SLIP_IN_PROGRESS_STATUS.UNKNOWN,
            action: PAYMENT_ACTION_STATUS.NONE,
            status: params.status === value ? PAYMENT_STATUS.UNKNOWN : value,
          }))
          break

        case 'inProgress':
          setSearchParams((params) => ({
            ...params,
            page: 1,
            status: PAYMENT_STATUS.IN_PROGRESS,
            action: PAYMENT_ACTION_STATUS.NONE,
            inProgress: params.inProgress === value ? SLIP_IN_PROGRESS_STATUS.UNKNOWN : value,
          }))
          break

        case 'orderType':
          setSearchParams((params) => ({
            ...params,
            page: 1,
            inProgress: SLIP_IN_PROGRESS_STATUS.UNKNOWN,
            action: PAYMENT_ACTION_STATUS.NONE,
            orderType: params.orderType === value ? ORDER_TYPE.UNKNOWN : value,
          }))
          break

        case 'action': {
          setSearchParams((params) => ({
            ...params,
            page: 1,
            orderType: ORDER_TYPE.UNKNOWN,
            status: PAYMENT_STATUS.UNKNOWN,
            inProgress: SLIP_IN_PROGRESS_STATUS.UNKNOWN,
            orderNo: '',
            'user.userNo': '',
            'user.firstName': '',
            'user.lastName': '',
            'user.phone': '',
            action: params.action === value ? PAYMENT_ACTION_STATUS.NONE : value,
          }))
          break
        }

        case 'orderNo':
        case 'user.userNo':
        case 'user.firstName':
        case 'user.lastName':
        case 'user.phone':
          setSearchParams((params) => ({
            ...params,
            page: 1,
            action: PAYMENT_ACTION_STATUS.UNKNOWN,
            orderNo: '',
            'user.userNo': '',
            'user.firstName': '',
            'user.lastName': '',
            'user.phone': '',
            [key]: value,
          }))
          break

        case 'ASC':
          setSearchParams((params) => ({
            ...params,
            sort: SORT_ORDER.ASC,
          }))
          break

        case 'DESC':
          setSearchParams((params) => ({
            ...params,
            sort: SORT_ORDER.DESC,
          }))
          break
        default:
          if (searchParams[key] === value) return

          setSearchParams((params) => ({
            ...params,
            page: 1,
            [key]: value,
          }))
          break
      }
    },
    [searchParams],
  )

  const handleClickResetFilter = () => {
    setSearchParams({
      ...searchParams,
      orderNo: '',
      'user.firstName': '',
      'user.lastName': '',
      'user.phone': '',
      'user.userNo': '',
    })
  }

  const handleRowClick = (payment) => {
    setSelectedPayment(payment)

    if (
      payment.action.status !== PAYMENT_ACTION_STATUS.BLOCKED &&
      (payment.status === PAYMENT_STATUS.IN_PROGRESS || payment.status === PAYMENT_STATUS.CONFIRM)
    ) {
      sendMessageWs(wsRef.current, payment.id, PAYMENT_ACTION_STATUS.IN_PROGRESS)
    }
  }

  const handleCloseModal = () => {
    if (
      selectedPayment.action.status !== PAYMENT_ACTION_STATUS.BLOCKED &&
      (selectedPayment.status === PAYMENT_STATUS.IN_PROGRESS || selectedPayment.status === PAYMENT_STATUS.CONFIRM)
    ) {
      sendMessageWs(wsRef.current, selectedPayment.id, PAYMENT_ACTION_STATUS.NONE)
    }

    setSelectedPayment(null)
  }

  const handleSearchQRTransactionNo = () => {
    setIsShowQrModal(true)
  }

  const handleCancelPayment = () => {
    setSearchParams({ ...searchParams })
    handleCloseModal()
  }

  const onApproveSlip = () => {
    setSearchParams({ ...searchParams })
  }

  const onRejectSlip = () => {
    setSearchParams({ ...searchParams })
  }

  const onApproveWithdraw = () => {
    setSearchParams({ ...searchParams })
  }

  const handleClickPagination = (isNext) => {
    const page = isNext ? searchParams.page + 1 : searchParams.page - 1
    setIsLoading(true)
    setSearchParams((params) => ({ ...params, page: page }))
  }

  const cancelOrderLottery = async () => {
    try {
      const response = await fetch(`${config.apiAdminGateway}/api/v2/admin/payments/cancelOpenLotteryAll`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${Cookies.get('accessToken')}`,
        },
      }).then((res) => res.json())

      if (response.status.code !== 0) {
        Swal.fire({
          icon: 'warning',
          title: 'คำเตือน',
          text: 'ไม่สามารถยกเลิกออเดอร์รอแจ้งโอนทั้งหมดได้',
          confirmButtonText: 'รับทราบ',
          reverseButtons: true,
        }).then(async (result) => {
          if (result.isConfirmed) {
            window.location.reload()
          }
        })
        throw new Error(response.status.description)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleClickCancelOrder = () => {
    Swal.fire({
      icon: 'warning',
      title: 'คำเตือน',
      text: 'คุณต้องการยกเลิกออเดอร์รอแจ้งโอนทั้งหมดหรือไม่',
      showCancelButton: true,
      confirmButtonText: 'ใช่',
      cancelButtonText: 'ไม่ใช่',
      reverseButtons: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        cancelOrderLottery()
        window.location.reload()
      }
    })
  }

  useEffect(() => {
    debouncedFetchPayments(searchParams)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  useEffect(() => {
    const setUpWebSocket = () => {
      wsRef.current = connectWs()

      wsRef.current.onmessage = (event) => {
        if (!isJsonString(event.data)) return

        const { id, action } = JSON.parse(JSON.parse(event.data))

        setSelectedPayment((payment) =>
          payment && payment.id === id ? { ...payment, action: { ...payment.action, status: action } } : payment,
        )

        setPayments((payments) =>
          payments.map((payment) => {
            return payment.id === id ? { ...payment, action: { ...payment.action, status: action } } : payment
          }),
        )
      }

      wsRef.current.onclose = () => {
        setUpWebSocket()
      }
    }

    setUpWebSocket()

    return () => {
      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close()
      }
    }
  }, [])

  return (
    <PageContainer>
      <Card>
        <Card.Header>
          <Card.Title>รายการชำระเงิน</Card.Title>
          {isRoundClosed && (
            <ButtonCancelOrder bgColor={Colors.LIGHT_RED} onClick={handleClickCancelOrder}>
              ยกเลิกออเดอร์รอแจ้งโอนทั้งหมด
            </ButtonCancelOrder>
          )}
        </Card.Header>
        <Card.Header>
          <PaymentFilter
            searchParams={searchParams}
            fetchPayments={debouncedFetchPayments}
            onUpdateSearchParams={handleUpdateSearchParams}
            onClickResetFilter={handleClickResetFilter}
            qrTransactionNo={qrTransactionNo}
            setQrTransactionNo={setQrTransactionNo}
            onClickSearchQRTransactionNo={handleSearchQRTransactionNo}
          />
        </Card.Header>
        <Card.Body
          style={{
            transition: 'background-color 200ms ease-in-out',
            backgroundColor:
              searchParams.action !== PAYMENT_ACTION_STATUS.NONE
                ? getBackgroundColorByActionStatus(searchParams.action)
                : getBackgroundColorByStatus(searchParams.status),
          }}
        >
          <StyledTable>
            <thead>
              <tr>
                <th>เลขที่ออเดอร์</th>
                <th>ประเภทออเดอร์</th>
                <th>สถานะ</th>
                <th>ยอดรวม</th>
                <th>คงเหลือ</th>
                <th>แอดมิน</th>
              </tr>
            </thead>
            <tbody>
              {payments.length > 0 ? (
                payments.map((payment) => {
                  const isLocked = payment.action.status !== PAYMENT_ACTION_STATUS.NONE

                  return (
                    <tr onClick={() => handleRowClick(payment)} key={payment.id} className={isLocked ? 'locked' : ''}>
                      <td>{payment.orderNo}</td>
                      <td>{payment.orderType}</td>
                      <td>
                        <StatusText color={getTextColorByStatus(payment.status)}>{payment.status}</StatusText>
                      </td>
                      <td>{Number(payment.total).toLocaleString('th-TH', { maximumFractionDigits: 2 })}</td>
                      <td>
                        {Number(payment.remainingAmount).toLocaleString('th-TH', {
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td>{isLocked && payment.action.adminName}</td>
                    </tr>
                  )
                })
              ) : (
                <tr style={{ pointerEvents: 'none' }}>
                  <td colSpan={99} style={{ textAlign: 'center', color: 'red', fontSize: '1.25rem' }}>
                    ไม่มีออเดอร์
                  </td>
                </tr>
              )}
            </tbody>
          </StyledTable>
        </Card.Body>
        <Card.Footer>
          <Pagination
            pagination={{ page: searchParams.page, hasNext: hasNextPage }}
            disabled={isLoading}
            onClickPagination={handleClickPagination}
          />
        </Card.Footer>
      </Card>
      <PaymentDetailModal
        payment={selectedPayment}
        setPayment={setSelectedPayment}
        onHide={handleCloseModal}
        onCancelPayment={handleCancelPayment}
        onApproveSlip={onApproveSlip}
        onRejectSlip={onRejectSlip}
        onApproveWithdraw={onApproveWithdraw}
        webSocketRef={wsRef}
      />
      <QRPaymentDetailModal
        isShow={isShowQrModal}
        onHide={() => setIsShowQrModal(false)}
        transactionNo={qrTransactionNo}
        onClickPayment={handleRowClick}
      />
    </PageContainer>
  )
}

export default Payment
